import QuotableStatsStrip from "@/components/QuotableStatsStrip";
import GovDoctypeDeepContent from "@/components/GovDoctypeDeepContent";
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { ServiceLanding, buildLandingSchemas } from "@/components/ServiceLanding";
import { canonicalAndHreflang, ctaDesc, ctaTitle } from "@/lib/seo";
import { findGovDoc, GOV_DOCTYPES } from "@/data/government-doctypes";


export const Route = createFileRoute("/certify/government/$doctype")({
  loader: ({ params }) => {
    const d = findGovDoc(params.doctype);
    if (!d) throw notFound();
    return { d };
  },
  head: ({ params }) => {
    const d = findGovDoc(params.doctype);
    if (!d) return { meta: [{ title: ctaTitle("Not found", "/certify/government/$doctype", "th")}, { name: "robots", content: "noindex" }] };
    const path = `/certify/government/${d.slug}`;
    const TITLE = `รับรอง${d.th} — แปล + MFA + Apostille | NYC`;
    const DESC = `บริการแปล + รับรอง${d.th} (${d.en}) โดยกรมการกงสุล MFA + Apostille (Hague 15 ธ.ค. 2025) เสร็จ ${d.turnaround} ใช้ยื่นวีซ่า/หน่วยงานต่างประเทศได้ทันที · ${d.usage} — ทักไลน์เพื่อรับเช็กลิสต์เอกสารทันที`;
    return {
      meta: [
        { title: ctaTitle(TITLE, "/certify/government/$doctype", "th")}, { name: "description", content: ctaDesc(DESC, "/certify/government/$doctype", "th")},
        { property: "og:title", content: ctaTitle(TITLE, "/certify/government/$doctype", "th")}, { property: "og:description", content: ctaDesc(DESC, "/certify/government/$doctype", "th")},
        { property: "og:url", content: `https://nyclanguageinstitute.com${path}` },
      ],
      links: canonicalAndHreflang(path, `/en${path}`),
      scripts: buildLandingSchemas({
        path, title: TITLE, description: DESC,
        serviceType: `Document Certification — ${d.en}`, priceFromThb: d.translatePrice + d.mfaFee,
        steps: [
          { name: "ตรวจต้นฉบับ", text: `ต้องเป็นฉบับที่${d.issuer}ออกให้ พร้อมตราประทับครบ` },
          { name: "แปลรับรอง", text: `แปลเป็นภาษาที่ปลายทางต้องการ (อังกฤษ/จีน/ญี่ปุ่น/EU sworn)` },
          { name: "MFA Legalization", text: "ประทับตรากรมการกงสุล กระทรวงการต่างประเทศ (แจ้งวัฒนะ)" },
          { name: "Apostille หรือ Embassy", text: "Apostille สำหรับ Hague members / Embassy Legalization สำหรับ non-Hague" },
          { name: "จัดส่ง", text: "EMS ฟรีในไทย · DHL/FedEx 2–5 วันทั่วโลก" },
        ],
        faqs: [
          { q: `${d.th}ใช้ทำอะไรในต่างประเทศ?`, a: d.usage },
          { q: "ข้อผิดพลาดที่พบบ่อย?", a: d.pitfall },
          { q: "ราคาและเวลา?", a: `แปล ฿${d.translatePrice.toLocaleString()} + MFA ฿${d.mfaFee.toLocaleString()} = เริ่ม ฿${(d.translatePrice + d.mfaFee).toLocaleString()} เสร็จ ${d.turnaround}` },
          { q: "หน่วยงานที่ออก?", a: d.issuer },
          { q: "Apostille หรือ Embassy?", a: "Apostille ใช้ได้กับ 130 ประเทศสมาชิก Hague (จีน UAE เข้าใหม่ปี 2023–2024) — non-Hague ใช้ Embassy Legalization" },
        ],
        breadcrumbs: [
          { name: "Home", path: "/" },
          { name: "รับรองเอกสารราชการ", path: "/certify/government" },
          { name: d.th, path },
        ],
      }),
    };
  },
  component: Page,
  notFoundComponent: () => (
    <main className="p-12"><h1 className="text-2xl font-bold">ไม่พบเอกสารประเภทนี้</h1>
    <Link to="/certify/government/$doctype" params={{ doctype: "birth-certificate" }} className="text-primary underline mt-4 block">← ดูรายการเอกสารทั้งหมด</Link>
          <QuotableStatsStrip cluster="translate" locale="th" variant="light" />
    </main>
  ),
});

function Page() {
  const { d } = Route.useLoaderData();
  return (
    <>
    <ServiceLanding
      badge="Gov · MFA · Apostille"
      title={`รับรอง${d.th}`}
      subtitle={`${d.en} — ${d.usage}`}
      quickAnswer={{
        q: `${d.th}ต้องรับรองอะไรบ้างก่อนใช้ต่างประเทศ?`,
        a: `แปล + MFA (กรมการกงสุล) + ${true ? "Apostille (Hague)" : "Embassy Legalization"} — เริ่ม ฿${(d.translatePrice + d.mfaFee).toLocaleString()} เสร็จ ${d.turnaround} · ${d.usage}`,
      }}
      heroBullets={[
        `ออกโดย ${d.issuer}`,
        `เริ่ม ฿${(d.translatePrice + d.mfaFee).toLocaleString()}`,
        `เสร็จ ${d.turnaround}`,
        "รวมแปล + MFA + จัดส่ง EMS",
      ]}
      whoFor={{
        th: ["ผู้ยื่นวีซ่า/เอกสารในต่างประเทศ", "นักเรียนที่ต้องรับรองเอกสารการศึกษา", "คู่สมรสระหว่างประเทศ"],
        foreign: [`Foreigners requesting Thai ${d.en} for use abroad`],
      }}
      steps={[
        { name: "ตรวจต้นฉบับ", text: `ต้นฉบับจาก ${d.issuer}` },
        { name: "แปลรับรอง", text: "โดยนักแปลขึ้นทะเบียน MFA/MoJ" },
        { name: "MFA legalization", text: "กรมการกงสุล แจ้งวัฒนะ" },
        { name: "Apostille หรือ Embassy", text: "ตามประเทศปลายทาง" },
        { name: "จัดส่ง", text: "EMS/DHL ทั่วโลก" },
      ]}
      pricing={[
        { label: `แปล ${d.th}`, price: d.translatePrice.toLocaleString(), note: "ต่อฉบับ" },
        { label: "MFA Legalization", price: d.mfaFee.toLocaleString(), note: "ค่าธรรมเนียม + processing" },
        { label: "Apostille (Hague)", price: "1,500", note: "130 ประเทศ" },
        { label: "Embassy Legalization", price: "1,200+", note: "non-Hague" },
        { label: "Express 24 ชม.", price: "+50%", note: "ตามคิว MFA" },
      ]}
      faqs={[
        { q: "ใช้ทำอะไร?", a: d.usage },
        { q: "ข้อผิดพลาดที่พบบ่อย?", a: d.pitfall },
        { q: "หน่วยงานที่ออก?", a: d.issuer },
        { q: "ราคาและเวลา?", a: `฿${(d.translatePrice + d.mfaFee).toLocaleString()}+ · ${d.turnaround}` },
        { q: "Apostille กับ Embassy ต่างกันอย่างไร?", a: "Apostille ใช้ในประเทศสมาชิก Hague (130 ประเทศ ล่าสุดจีน + UAE) — non-Hague ต้อง Embassy Legalization ที่สถานทูตปลายทางในไทย" },
      ]}
      relatedLinks={[
        ...GOV_DOCTYPES.filter((x) => x.slug !== d.slug).slice(0, 6).map((x) => ({
          to: `/certify/government/${x.slug}` as const, label: `รับรอง${x.th}`,
        })) as any,
        { to: "/apostille", label: "Apostille service" },
        { to: "/translate/mfa", label: "แปล + MFA" },
        { to: "/embassy", label: "Embassy Legalization" },
      ]}
    />
    <GovDoctypeDeepContent d={d} lang="th" />
    </>
  );
}
